import React, {useContext, useMemo, useState} from "react";
import Tr from "translations/Translations";
import PropTypes from "prop-types";
import Dialog from "components/Dialog";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import GridBlock from "components/GridBlock";
import BoldTypography from "components/BoldTypography";
import Form from "components/Form";
import useId from "hooks/useId";
import VS from "constants/ValidationShema";
import {connect} from "react-redux";
import PartsTable from "components/PartsTable";
import {
    OfferPositionStatusesReadyToDelete,
    OfferPositionStatusesReadyToOffering,
    OfferPositionStatusesReadyToOfferSign,
    OfferPositionStatusesReadyToWithdraw,
    OfferPositionStatusesReadyToWithdrawSign
} from "./constants/OfferPositionStatusType";
import * as Yup from "yup";
import Typography from "components/Typography";
import {makeStyles} from "@material-ui/styles";
import {OfferStatusType} from "./constants/OfferStatusType";
import TextField from "@material-ui/core/TextField/TextField";
import useDebouncedEffect from "hooks/useDebouncedEffect";
import OfferStageTranslationsContext from "context/OfferStageTranslationsContext";
import PartsRadioTable from "components/PartsRadioTable";

const useStyle = makeStyles(theme => ({
    root: {},
    info: {
        fontSize: "small",
        fontWeight: "bold",
        textAlign: "center",
        marginTop: theme.spacing()
    },
    search: {
        width: "90%"
    },
}))

export const POPUP_MODE = {
    OFFER: "OFFER",
    OFFER_SIGN: "OFFER_SIGN",
    OFFER_BREAK: "OFFER_BREAK",
    WITHDRAW: "WITHDRAW",
    WITHDRAW_SIGN: "WITHDRAW_SIGN",
    WITHDRAW_BREAK: "WITHDRAW_BREAK",
    OPERATOR_PLACE: "OPERATOR_PLACE",
    DELETE_OFFER: "DELETE_OFFER",
}

const isSupported = (mode, position) => {
    switch (mode) {
        case POPUP_MODE.OFFER:
        case POPUP_MODE.OPERATOR_PLACE:
            return OfferPositionStatusesReadyToOffering.includes(position.status)
        case POPUP_MODE.OFFER_SIGN:
        case POPUP_MODE.OFFER_BREAK:
            return OfferPositionStatusesReadyToOfferSign.includes(position.status);
        case POPUP_MODE.WITHDRAW:
            return OfferPositionStatusesReadyToWithdraw.includes(position.status);
        case POPUP_MODE.WITHDRAW_SIGN:
        case POPUP_MODE.WITHDRAW_BREAK:
            return OfferPositionStatusesReadyToWithdrawSign.includes(position.status);
        case POPUP_MODE.DELETE_OFFER:
            return (!position.contractorOfferStatus && OfferPositionStatusesReadyToDelete.includes(position.status))
                || (position.contractorOfferStatus === OfferStatusType.NORMAL && position.offerDate === null && position.offerTmpDate !== null)
        default:
            throw new Error("Unknown PopupMode: " + mode)
    }

}
const getButtonText = (mode, digitalSign, stageTr) => {
    switch (mode) {
        case POPUP_MODE.OFFER:
        case POPUP_MODE.OFFER_SIGN:
            return stageTr(digitalSign ? "sign_offer_form" : "place_offer")
        case POPUP_MODE.OPERATOR_PLACE:
            return stageTr("place_offer_operator")
        case POPUP_MODE.OFFER_BREAK:
            return stageTr("sign_offer_form_stop")
        case POPUP_MODE.WITHDRAW:
        case POPUP_MODE.WITHDRAW_SIGN:
            return stageTr(digitalSign ? "sign_withdraw_form" : "withdraw_offer")
        case POPUP_MODE.WITHDRAW_BREAK:
            return stageTr("sign_withdraw_form_stop")
        case POPUP_MODE.DELETE_OFFER:
            return stageTr("delete_offer")
        default:
            throw new Error("Unknown PopupMode: " + mode)
    }
}
const getListText = (mode, stageTr) => {
    switch (mode) {
        case POPUP_MODE.OFFER:
        case POPUP_MODE.OFFER_SIGN:
            return stageTr("choose_positions_to_offer")
        case POPUP_MODE.OPERATOR_PLACE:
            return stageTr("choose_positions_to_offer_operator")
        case POPUP_MODE.WITHDRAW:
        case POPUP_MODE.WITHDRAW_SIGN:
            return stageTr("choose_positions_to_withdraw")
        case POPUP_MODE.OFFER_BREAK:
        case POPUP_MODE.WITHDRAW_BREAK:
        case POPUP_MODE.DELETE_OFFER:
            return Tr("choose_position")
        default:
            throw new Error("Unknown PopupMode: " + mode)
    }
}


const OfferPositionsSelectPopup = (
    {
        onCancel,
        onSuccess,
        offerEdit,
        popupMode,
        additionalOffers = false,
        digitalSign,
        formSubmit
    }) => {
    const classes = useStyle();
    const formId = useId();
    const handleSubmit = () => formSubmit[formId]();
    const stageTr = useContext(OfferStageTranslationsContext);
    const positions = useMemo(() => offerEdit.positions.filter(x => isSupported(popupMode, x))
        .map(x => ({
            ...x,
            value: "_" + x.identity,
            label: Tr("part_x", {x: x.order}) + ": " + x.name,
            search: Tr("part_x", {x: x.order}) + ": " + x.name,
        })), [offerEdit, popupMode]);

    const [positionsFiltered, setPositionsFiltered] = useState(positions);
    const [searchText, setSearchText] = useState("");

    const onSubmit = (values) => {
        let posIds;
        if (additionalOffers) {
            posIds = positions.filter(x => x.value === values.part).map(x => x.positionId)
        } else {
            posIds = positions.filter(x => values.parts[x.identity]).map(x => x.positionId)
        }
        onSuccess(posIds);
    }

    const getButtons = () => {
        const buttons = [];
        buttons.push(createButton(Tr("cancel"), onCancel, "outlined"));
        buttons.push(createButton(getButtonText(popupMode, digitalSign, stageTr), handleSubmit, undefined, !positions.length));
        return buttons;
    }

    const validationSchema = Yup.object().shape({
        parts: !additionalOffers ? VS.any().test("partSelected", Tr("choose_at_least_one_position"), (e) => {
            const selIndex = Object.keys(e).findIndex(x => e[x] === true);
            return selIndex > -1;
        }) : VS.any(),
        part: additionalOffers ? VS.string(undefined, "choose_position") : VS.any()
    })

    useDebouncedEffect(() => {
        if (!searchText || searchText === "") {
            if (positions !== positionsFiltered) {
                setPositionsFiltered(positions);
            }
        } else {
            const reg = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
            setPositionsFiltered(positions.filter(pos => pos.search.search(reg) > -1));
        }
    }, {timeout: 800, ignoreInitialCall: true}, [searchText])


    return (
        <Dialog
            open
            title={<TextField className={classes.search} value={searchText} onChange={(e) => setSearchText(e.target.value)} label={Tr("search2")}/>}
            dialogActions={<>
                <GridBlock column>
                    <MultiButton buttons={getButtons()}/>
                    {POPUP_MODE.OFFER === popupMode &&
                        <Typography className={classes.info}>*{stageTr("place_offer_confirm")}</Typography>
                    }
                </GridBlock>
            </>}
            maxWidth="md"
        >
            <GridBlock
                column
            >
                <Form
                    initialValues={{
                        parts: !additionalOffers ? positions.reduce((prev, cur) => {
                            prev[cur.identity] = false;
                            return prev;
                        }, {}) : null,
                        part: null
                    }}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    id={formId}
                >
                    {(formikProps) => (
                        <GridBlock
                            column
                        >
                            {
                                additionalOffers ?
                                    <PartsRadioTable
                                        label={getListText(popupMode, stageTr)}
                                        id="part"
                                        formikProps={formikProps}
                                        elementList={positionsFiltered}
                                    /> :
                                    <PartsTable
                                        label={getListText(popupMode, stageTr)}
                                        id="parts"
                                        formikProps={formikProps}
                                        elementList={positionsFiltered}
                                    />
                            }
                            {
                                !(positionsFiltered.length) &&
                                <BoldTypography variant="body1">
                                    {
                                        Tr("no_options")
                                    }
                                </BoldTypography>
                            }
                        </GridBlock>
                    )}
                </Form>

            </GridBlock>
        </Dialog>)
}

OfferPositionsSelectPopup.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    offerEdit: PropTypes.shape({
        positions: PropTypes.arrayOf(PropTypes.shape({
            identity: PropTypes.number.isRequired,
            positionId: PropTypes.number.isRequired,
            order: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
        })).isRequired
    }).isRequired,
    digitalSign: PropTypes.bool.isRequired,
    popupMode: PropTypes.oneOf(Object.keys(POPUP_MODE)),
    additionalOffers: PropTypes.bool
}

export default connect((state) => ({
    formSubmit: state.form
}), {})(OfferPositionsSelectPopup)